import { useEffect, useMemo, useRef, useState } from 'react'
import { openPrintHtml, request } from './StockAdmin'

/** Nastavenie tlačových zostáv – čo sa vytlačí pri položkách jednotlivých dokladov, v akom poradí, akou šírkou/výškou/písmom. */

type Column = 'allergens' | 'weight' | 'shelfLife' | 'discount' | 'costPrice' | 'costTotal' | 'priceExcl' | 'priceIncl' | 'vatRate' | 'totalExcl' | 'totalIncl'
type ColumnWidths = Partial<Record<Column, number>>
type DocDef = { label: string; group: 'sales' | 'stock' | 'order'; columns: Column[]; locked: Column[]; defaults: Column[] }
type DocSettings = { columns: Column[]; columnWidths: ColumnWidths; rowHeight: number | null; fontSize: number | null; fontWeight: 'normal' | 'bold' | null; showSignatures: boolean; showAddress: boolean; footerText: string }
type Settings = Record<string, DocSettings>

const GROUPS: Array<[DocDef['group'], string]> = [['sales', 'Obchodné doklady'], ['stock', 'Skladové doklady'], ['order', 'Objednávky e-shopu']]
const ICONS: Record<string, string> = { ESHOP_INVOICE: '🛒', QUOTE: '💬', PROFORMA: '💶', DELIVERY_NOTE: '📦', INVOICE: '🧾', CREDIT_NOTE: '↩', ADVANCE_TAX: '✓', RECEIPT: '↘', ISSUE: '↗', TRANSFER: '⇄', ORDER: '🛍' }
const DETAIL: Column[] = ['allergens', 'weight', 'shelfLife']
const HINT: Partial<Record<Column, string>> = {
  allergens: 'pod názvom položky', weight: 'pod názvom položky', shelfLife: 'pod názvom položky',
  costPrice: 'nákupná / skladová', costTotal: 'množstvo × skladová cena',
}
// musí zodpovedať medziam v src/print-settings.ts (COLUMN_WIDTH_MIN/MAX, ROW_HEIGHT_MIN/MAX, FONT_SIZE_MIN/MAX)
const WIDTH_MIN = 40, WIDTH_MAX = 220, ROW_MIN = 14, ROW_MAX = 80, FONT_MIN = 6, FONT_MAX = 24
// ukážkový riadok pre náhľad
const SAMPLE = { name: 'Malinová torta – 12 porcií', qty: '2 ks', allergens: '1, 3, 7', weight: '1,8 kg', shelfLife: '3 dni pri 2–6 °C', discount: '5 %', costPrice: '14,20 €', costTotal: '28,40 €', priceExcl: '29,27 €', priceIncl: '36,00 €', vatRate: '23 %', totalExcl: '55,61 €', totalIncl: '68,40 €' }

export default function PrintSettingsAdmin() {
  const [docs, setDocs] = useState<Record<string, DocDef>>({})
  const [labels, setLabels] = useState<Record<Column, string> | null>(null)
  const [defaults, setDefaults] = useState<Settings>({})
  const [saved, setSaved] = useState<Settings>({})
  const [draft, setDraft] = useState<Settings>({})
  const [kind, setKind] = useState('INVOICE')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [dragIndex, setDragIndex] = useState<number | null>(null)

  useEffect(() => {
    request('/api/admin/print-settings').then(d => { setDocs(d.docs); setLabels(d.columns); setDefaults(d.defaults); setSaved(d.settings); setDraft(d.settings) })
      .catch(e => setError(e instanceof Error ? e.message : 'Nastavenie sa nepodarilo načítať.'))
  }, [])

  const def = docs[kind]
  const current = draft[kind]
  const dirty = JSON.stringify(draft) !== JSON.stringify(saved)
  const changedKinds = useMemo(() => new Set(Object.keys(draft).filter(k => JSON.stringify(draft[k]) !== JSON.stringify(saved[k]))), [draft, saved])
  const set = (patch: Partial<DocSettings>) => { setDraft(d => ({ ...d, [kind]: { ...d[kind], ...patch } })); setNotice('') }
  const toggleDetail = (c: Column) => {
    if (!def) return
    const on = current.columns.includes(c)
    set({ columns: on ? current.columns.filter(x => x !== c) : [...current.columns, c] })
  }

  async function save() {
    setBusy(true); setError(''); setNotice('')
    try {
      const d = await request('/api/admin/print-settings', { method: 'PUT', body: JSON.stringify({ settings: draft }) })
      setSaved(d.settings); setDraft(d.settings); setNotice('Uložené – nové nastavenie platí pri ďalšej tlači.')
    } catch (e) { setError(e instanceof Error ? e.message : 'Uloženie sa nepodarilo.') } finally { setBusy(false) }
  }

  // Náhľad na poslednom skutočnom doklade – s práve upraveným (aj neuloženým) nastavením.
  const [preview, setPreview] = useState<{ html: string | null; message?: string; document?: string; missing?: { total: number; allergens: number; weight: number; shelfLife: number } } | null>(null)
  const frame = useRef<HTMLIFrameElement>(null)
  // Ťahanie okraja stĺpca/riadku priamo v ukážke – ako v Exceli. Musí byť nad "if (!current) return"
  // nižšie (Pravidlá hookov – inak sa tento useRef pri prvom vykreslení vynechá a React potom spadne).
  type Drag = { kind: 'col'; col: Column; startX: number; startWidth: number } | { kind: 'row'; startY: number; startHeight: number }
  const dragRef = useRef<Drag | null>(null)
  useEffect(() => {
    if (!current) return
    let alive = true
    const t = window.setTimeout(() => {
      request('/api/admin/print-settings/preview', { method: 'POST', body: JSON.stringify({ kind, settings: current }) })
        .then(d => { if (alive) setPreview(d) }).catch(e => { if (alive) setPreview({ html: null, message: e instanceof Error ? e.message : 'Náhľad sa nepodarilo načítať.' }) })
    }, 250)
    return () => { alive = false; window.clearTimeout(t) }
  }, [kind, JSON.stringify(current)]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!labels || !def || !current) return <div className="panel"><p className="empty-message">{error || 'Načítavam nastavenie tlače…'}</p></div>

  // Stĺpce TABUĽKY (číselné – cena, zľava, DPH, súčty…) sú usporiadateľné ťahaním; doplnkové údaje (alergény/hmotnosť/trvanlivosť) sa tlačia pod názvom a ich poradie je pevné.
  const tableCols = current.columns.filter(c => !DETAIL.includes(c))
  const offCols = def.columns.filter(c => !DETAIL.includes(c) && !tableCols.includes(c))
  const setTableCols = (next: Column[]) => set({ columns: [...next, ...current.columns.filter(c => DETAIL.includes(c))] })
  const moveCol = (from: number, to: number) => {
    if (from === to || from < 0 || to < 0 || from >= tableCols.length || to >= tableCols.length) return
    const next = [...tableCols]
    const [item] = next.splice(from, 1)
    next.splice(to, 0, item)
    setTableCols(next)
  }
  const removeCol = (c: Column) => { if (def.locked.includes(c)) return; setTableCols(tableCols.filter(x => x !== c)) }
  const addCol = (c: Column) => { if (tableCols.includes(c)) return; setTableCols([...tableCols, c]) }
  const setWidth = (c: Column, px: number | null) => {
    const widths = { ...current.columnWidths }
    if (px === null) delete widths[c]; else widths[c] = px
    set({ columnWidths: widths })
  }

  const onDragMove = (e: MouseEvent) => {
    const d = dragRef.current
    if (!d) return
    if (d.kind === 'col') setWidth(d.col, Math.min(WIDTH_MAX, Math.max(WIDTH_MIN, Math.round(d.startWidth + (e.clientX - d.startX)))))
    else set({ rowHeight: Math.min(ROW_MAX, Math.max(ROW_MIN, Math.round(d.startHeight + (e.clientY - d.startY)))) })
  }
  const stopDrag = () => { dragRef.current = null; window.removeEventListener('mousemove', onDragMove); window.removeEventListener('mouseup', stopDrag) }
  const startColDrag = (c: Column, e: React.MouseEvent) => {
    e.preventDefault()
    const th = (e.currentTarget as HTMLElement).parentElement as HTMLElement | null
    dragRef.current = { kind: 'col', col: c, startX: e.clientX, startWidth: current.columnWidths[c] ?? th?.offsetWidth ?? 90 }
    window.addEventListener('mousemove', onDragMove); window.addEventListener('mouseup', stopDrag)
  }
  const startRowDrag = (e: React.MouseEvent) => {
    e.preventDefault()
    dragRef.current = { kind: 'row', startY: e.clientY, startHeight: current.rowHeight ?? 32 }
    window.addEventListener('mousemove', onDragMove); window.addEventListener('mouseup', stopDrag)
  }

  const rowStyle: React.CSSProperties = { ...(current.rowHeight ? { height: current.rowHeight } : {}) }
  const tableStyle: React.CSSProperties = { ...(current.fontSize ? { fontSize: current.fontSize } : {}), ...(current.fontWeight ? { fontWeight: current.fontWeight } : {}) }
  const colStyle = (c: Column): React.CSSProperties => current.columnWidths[c] ? { width: current.columnWidths[c] } : {}

  return <div className="ps">
    <aside className="ps-list panel">
      {GROUPS.map(([group, title]) => <div key={group} className="ps-group">
        <h3>{title}</h3>
        {Object.entries(docs).filter(([, d]) => d.group === group).map(([k, d]) => <button key={k} type="button" className={`ps-doc ${k === kind ? 'is-on' : ''}`} onClick={() => setKind(k)}>
          <span className="ps-doc-icon" aria-hidden="true">{ICONS[k] ?? '▤'}</span>
          <span><strong>{d.label}</strong><small>{draft[k]?.columns.length ?? 0} údajov{changedKinds.has(k) ? ' · neuložené' : ''}</small></span>
        </button>)}
      </div>)}
    </aside>

    <section className="ps-main panel">
      <header className="ps-head">
        <div><p>Tlačová zostava</p><h2>{ICONS[kind]} {def.label}</h2></div>
        <div className="ps-head-actions">
          <button type="button" className="pa-ghost" onClick={() => set(defaults[kind])}>Predvolené</button>
          <button type="button" className="primary-btn" disabled={busy || !dirty} onClick={() => void save()}>{busy ? 'Ukladám…' : dirty ? `Uložiť${changedKinds.size > 1 ? ` (${changedKinds.size} doklady)` : ''}` : 'Uložené'}</button>
        </div>
      </header>
      {error && <p className="error-message" role="alert">{error}</p>}
      {notice && <p className="success-message" role="status">{notice}</p>}

      <h4>Stĺpce tabuľky <small>– ťahaním (alebo šípkami) zmeníte poradie, číslom šírku v px</small></h4>
      <ul className="ps-cols">
        {tableCols.map((c, i) => {
          const locked = def.locked.includes(c)
          return <li key={c} draggable className={`ps-col ${dragIndex === i ? 'is-dragging' : ''}`}
            onDragStart={() => setDragIndex(i)}
            onDragOver={e => e.preventDefault()}
            onDrop={() => { if (dragIndex !== null) moveCol(dragIndex, i); setDragIndex(null) }}
            onDragEnd={() => setDragIndex(null)}>
            <span className="ps-col-handle" aria-hidden="true">⠿</span>
            <span className="ps-col-order">
              <button type="button" aria-label={`${labels[c]} – posunúť vyššie`} disabled={i === 0} onClick={() => moveCol(i, i - 1)}>▲</button>
              <button type="button" aria-label={`${labels[c]} – posunúť nižšie`} disabled={i === tableCols.length - 1} onClick={() => moveCol(i, i + 1)}>▼</button>
            </span>
            <strong>{labels[c]}</strong>
            {locked && <span className="ps-lock" title="Povinný údaj na daňovom doklade (§ 74 zákona o DPH)">🔒</span>}
            <label className="ps-col-width">šírka <input type="number" min={WIDTH_MIN} max={WIDTH_MAX} step={5} placeholder="auto" value={current.columnWidths[c] ?? ''} onChange={e => setWidth(c, e.target.value ? Math.min(WIDTH_MAX, Math.max(WIDTH_MIN, Number(e.target.value))) : null)} /> px</label>
            {!locked && <button type="button" className="ps-col-remove" aria-label={`Odstrániť ${labels[c]}`} onClick={() => removeCol(c)}>×</button>}
          </li>
        })}
        {!tableCols.length && <li className="ps-col-empty">Žiadny stĺpec – pridajte nižšie.</li>}
      </ul>
      {offCols.length > 0 && <div className="ps-cols-add">
        <span>Pridať stĺpec:</span>
        {offCols.map(c => <button key={c} type="button" className="pa-ghost ps-col-add-btn" onClick={() => addCol(c)}>+ {labels[c]}</button>)}
      </div>}

      <h4>Doplnkové údaje pod názvom položky</h4>
      <div className="ps-chips">{DETAIL.map(c => {
        const on = current.columns.includes(c)
        return <button key={c} type="button" role="switch" aria-checked={on} className={`ps-chip ${on ? 'is-on' : ''}`} onClick={() => toggleDetail(c)}>
          <span className="ps-check" aria-hidden="true">{on ? '✓' : ''}</span>
          <span><strong>{labels[c]}</strong>{HINT[c] && <small>{HINT[c]}</small>}</span>
        </button>
      })}</div>
      {def.locked.length > 0 && <p className="ps-note">🔒 Faktúra, dobropis a daňový doklad musia podľa zákona obsahovať jednotkovú cenu bez DPH, sadzbu DPH a základ – tieto údaje sa vytlačia vždy.</p>}

      <h4>Vzhľad riadku položky</h4>
      <div className="ps-appearance">
        <label>Výška riadku <small>px</small>
          <div className="ps-appearance-row"><input type="range" min={ROW_MIN} max={ROW_MAX} value={current.rowHeight ?? 24} onChange={e => set({ rowHeight: Number(e.target.value) })} /><span>{current.rowHeight ?? 'predvolená'}</span>{current.rowHeight !== null && <button type="button" className="pa-link" onClick={() => set({ rowHeight: null })}>predvolená</button>}</div>
        </label>
        <label>Veľkosť písma <small>px</small>
          <div className="ps-appearance-row"><input type="range" min={FONT_MIN} max={FONT_MAX} value={current.fontSize ?? 12} onChange={e => set({ fontSize: Number(e.target.value) })} /><span>{current.fontSize ?? 'predvolená'}</span>{current.fontSize !== null && <button type="button" className="pa-link" onClick={() => set({ fontSize: null })}>predvolená</button>}</div>
        </label>
        <label>Hrúbka písma
          <div className="ps-appearance-row ps-fontweight">
            <button type="button" className={!current.fontWeight ? 'is-on' : ''} onClick={() => set({ fontWeight: null })}>Predvolená</button>
            <button type="button" className={current.fontWeight === 'normal' ? 'is-on' : ''} onClick={() => set({ fontWeight: 'normal' })}>Normálne</button>
            <button type="button" className={current.fontWeight === 'bold' ? 'is-on' : ''} onClick={() => set({ fontWeight: 'bold' })}><b>Tučné</b></button>
          </div>
        </label>
      </div>

      <div className="ps-row">
        <label className="ps-switch"><input type="checkbox" checked={current.showSignatures} onChange={e => set({ showSignatures: e.target.checked })} /> {kind === 'ORDER' ? 'Riadok „Pripravil / Vydal“' : 'Podpisové riadky (vystavil, prevzal)'}</label>
        {kind === 'ORDER' && <label className="ps-switch"><input type="checkbox" checked={current.showAddress} onChange={e => set({ showAddress: e.target.checked })} /> Adresa zákazníka (ak ju máme)</label>}
      </div>
      <label className="ps-footer">Text na konci dokladu <small>(nepovinné, napr. „Ďakujeme za objednávku“ alebo upozornenie na skladovanie)</small>
        <textarea rows={2} maxLength={300} value={current.footerText} onChange={e => set({ footerText: e.target.value })} placeholder="Bez textu" />
      </label>

      <div className="ps-live-head"><h4>Náhľad na skutočnom doklade{preview?.document ? <small> – {preview.document}</small> : null}</h4>
        {preview?.html && <button type="button" className="pa-ghost" onClick={() => openPrintHtml(preview.html!, `${def.label} ${preview.document ?? ''}`)}>⤢ Zväčšiť / tlačiť</button>}</div>
      {preview?.missing && (() => {
        const m = preview.missing
        const gaps = DETAIL.filter(c => current.columns.includes(c) && m[c as 'allergens' | 'weight' | 'shelfLife'] > 0)
          .map(c => `${labels[c].toLowerCase()} (${m[c as 'allergens' | 'weight' | 'shelfLife']} z ${m.total})`)
        return gaps.length ? <p className="ps-note ps-warn">ℹ Na tomto doklade chýba v karte produktu: {gaps.join(', ')}. Pri takých položkách sa údaj nevytlačí – doplňte ho v časti <b>Produkty</b> (úprava produktu) a na doklade sa zobrazí.</p> : null
      })()}
      {preview?.html ? <div className="ps-live"><iframe ref={frame} title="Náhľad dokladu" srcDoc={preview.html} onLoad={() => { try { const bar = frame.current?.contentDocument?.querySelector('.toolbar') as HTMLElement | null; if (bar) bar.style.display = 'none' } catch { /* nevadí */ } }} /></div> : <p className="ps-note">{preview?.message ?? 'Načítavam náhľad…'}</p>}

      <h4>Ukážka položky <small>– ako v Exceli: ťahajte okraj stĺpca alebo riadku</small></h4>
      <div className="ps-preview">
        <table style={tableStyle} className="ps-excel">
          <thead><tr><th className="r">Množstvo</th><th>Položka</th>{tableCols.map(c => <th key={c} className="r" style={colStyle(c)}>{labels[c].replace('Sadzba DPH', 'DPH')}<span className="ps-col-handle-drag" title="Ťahaním zmeníte šírku stĺpca" onMouseDown={e => startColDrag(c, e)} /></th>)}</tr></thead>
          <tbody><tr style={rowStyle}><td className="r">{SAMPLE.qty}</td><td><strong>{SAMPLE.name}</strong>{DETAIL.some(c => current.columns.includes(c)) && <small>{DETAIL.filter(c => current.columns.includes(c)).map(c => c === 'allergens' ? `alergény: ${SAMPLE.allergens}` : c === 'weight' ? SAMPLE.weight : `trvanlivosť ${SAMPLE.shelfLife}`).join(' · ')}</small>}</td>{tableCols.map(c => <td key={c} className="r" style={colStyle(c)}>{SAMPLE[c as keyof typeof SAMPLE]}</td>)}</tr></tbody>
        </table>
        <div className="ps-row-handle-drag" title="Ťahaním zmeníte výšku riadku" onMouseDown={startRowDrag}>↕ výška riadku</div>
        {current.footerText && <p className="ps-preview-foot">{current.footerText}</p>}
        {current.showSignatures && <div className="ps-preview-sign"><span>{kind === 'ORDER' ? 'Pripravil' : 'Vystavil'}</span><span>{kind === 'ORDER' ? 'Vydal' : 'Prevzal'}</span></div>}
      </div>
    </section>
  </div>
}
